import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(sp.get("signup") != null ? "register" : "login"); // login | register | magic
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [accountType, setAccountType] = useState(null); // "contestant" | "fan" (register only)
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // null | { kind: "login"|"reset", email }
  const [cfg, setCfg] = useState({ magic_link_minutes: 15, reset_minutes: 30 });
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);

  useEffect(() => { api.get("/auth/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => () => clearInterval(timer.current), []);

  const startCooldown = () => {
    setCooldown(30);
    clearInterval(timer.current);
    timer.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timer.current); return 0; } return c - 1; }), 1000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const email = form.email.trim().toLowerCase();
    try {
      if (mode === "login") {
        await api.post("/auth/request-link", { email, origin_url: window.location.origin });
        setSent({ kind: "login", email });
        startCooldown();
      } else {
        await api.post("/auth/register-passwordless", { name: form.name.trim(), email, origin_url: window.location.origin, account_type: accountType || "contestant" });
        setSent({ kind: "signup", email });
        startCooldown();
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0 || !sent) return;
    setLoading(true);
    try {
      await api.post(sent.kind === "reset" ? "/auth/forgot-password" : "/auth/request-link", { email: sent.email, origin_url: window.location.origin });
      startCooldown();
      toast.success("Sent again — check your inbox");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const input = "w-full bg-white border border-[#D7DBE0] rounded-lg px-4 py-3 text-sm focus:border-[#C8000F] focus:ring-2 focus:ring-[#C8000F]/20 focus:outline-none transition";
  const minutes = sent?.kind === "reset" ? cfg.reset_minutes : cfg.magic_link_minutes;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0C1017] p-12 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-16 w-auto object-contain" />
        </div>
        <div>
          <h2 className="font-display text-white text-4xl font-extrabold leading-tight">Every rodeo,<br />every performance,<br /><span className="text-[#C8000F]">every draw.</span></h2>
          <p className="text-slate-400 mt-4 max-w-md">Plan your circuit, track the draw and find trades — all in one place, built for the road.</p>
        </div>
        <div className="text-slate-600 text-xs">© Rodeo Pilot</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md rp-fade-up">
          <div className="lg:hidden flex flex-col items-center text-center mb-8">
            <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-20 w-auto object-contain" />
            <p className="text-slate-500 text-sm mt-3 max-w-xs">Plan your whole rodeo season in one place.</p>
          </div>

          {sent ? (
            <div data-testid="auth-link-sent">
              <div className="w-14 h-14 rounded-full bg-[#C8000F]/10 flex items-center justify-center mb-5">
                <Mail className="w-7 h-7 text-[#C8000F]" />
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Check your email</h1>
              <p className="text-slate-500 text-sm mb-6">
                {sent.kind === "signup"
                  ? <>We've sent a sign-in link to <span className="font-semibold text-[#14181F]">{sent.email}</span> to finish setting up your account. Tap it and you're in — no password needed. It expires in {minutes} minutes.</>
                  : <>If <span className="font-semibold text-[#14181F]">{sent.email}</span> belongs to a Rodeo Pilot user, a {sent.kind === "reset" ? "password-reset" : "sign-in"} link is on its way. It expires in {minutes} minutes.</>}
              </p>
              <button onClick={resend} disabled={cooldown > 0 || loading} data-testid="auth-resend-btn"
                className="w-full border border-[#D7DBE0] hover:border-[#C8000F] text-[#14181F] font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend link"}
              </button>
              <button onClick={() => { setSent(null); setMode("login"); }} data-testid="auth-back-btn"
                className="w-full text-slate-500 hover:text-[#C8000F] text-sm font-semibold mt-4 flex items-center justify-center gap-1.5">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>
            </div>
          ) : (mode === "register" && !accountType) ? (
            <div data-testid="auth-type-choice">
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Join Rodeo Pilot</h1>
              <p className="text-slate-500 text-sm mb-7">First — are you a contestant competing, or a fan following the sport?</p>
              <div className="space-y-3">
                <button data-testid="auth-type-contestant" onClick={() => setAccountType("contestant")}
                  className="w-full text-left border-2 border-[#D7DBE0] hover:border-[#C8000F] rounded-xl p-4 transition group">
                  <div className="font-display text-lg font-extrabold text-[#14181F] group-hover:text-[#C8000F]">I'm a Contestant</div>
                  <div className="text-sm text-slate-500 mt-0.5">Track your draws, entries, deadlines, standings and trades.</div>
                </button>
                <button data-testid="auth-type-fan" onClick={() => setAccountType("fan")}
                  className="w-full text-left border-2 border-[#D7DBE0] hover:border-[#C8000F] rounded-xl p-4 transition group">
                  <div className="font-display text-lg font-extrabold text-[#14181F] group-hover:text-[#C8000F]">I'm a Fan</div>
                  <div className="text-sm text-slate-500 mt-0.5">Follow your favorite riders, get updates, and see where they compete next.</div>
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-6 text-center">
                Already have an account?{" "}
                <button data-testid="auth-toggle-btn" className="text-[#C8000F] font-semibold hover:underline" onClick={() => setMode("login")}>Sign in</button>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">{mode === "login" ? "Sign in" : (accountType === "fan" ? "Create your fan account" : "Create your contestant account")}</h1>
              <p className="text-slate-500 text-sm mb-8">{mode === "login" ? "Welcome back — enter your email and we'll send you a secure sign-in link. No password needed." : "Join the circuit in seconds — we'll email you a secure sign-in link, no password needed."}</p>

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && <input className={input} placeholder="Full name" required data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
                <input className={input} type="email" placeholder="Email" required data-testid="auth-email-input" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} <Mail className="w-4 h-4" /> Email me a sign-in link
                </button>
              </form>

              <p className="text-sm text-slate-500 mt-6 text-center">
                {mode === "login" ? "New here?" : "Already have an account?"}{" "}
                <button data-testid="auth-toggle-btn" className="text-[#C8000F] font-semibold hover:underline" onClick={() => setMode(mode === "login" ? "register" : "login")}>
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
